import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";

const CATEGORIES = ["travel", "food", "fuel", "hotel", "rental", "misc"];
const COLORS = ["#D97706", "#F59E0B", "#3B82F6", "#10B981", "#8B5CF6", "#EF4444"];

export default function Finance() {
  const [projects, setProjects] = useState([]);

  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);

  const totalInvoiced = projects.reduce((s, p) => s + (p.invoice_amount || 0), 0);
  const totalPaid = projects.reduce((s, p) => s + (p.amount_paid || 0), 0);
  const totalCrew = projects.reduce((s, p) => s + (p.crew_cost || 0), 0);
  const totalExp = projects.reduce((s, p) => s + (p.expense_total || 0), 0);
  const netProfit = totalPaid - (totalCrew + totalExp);

  const catTotals = {};
  for (const p of projects) {
    for (const e of (p.expenses || [])) {
      catTotals[e.category] = (catTotals[e.category] || 0) + (e.amount || 0);
    }
  }
  const catData = CATEGORIES.map((c, i) => ({ name: c, value: catTotals[c] || 0, color: COLORS[i] })).filter(x => x.value > 0);

  const projectData = projects.map(p => ({
    name: p.name.length > 18 ? p.name.slice(0, 16) + "…" : p.name,
    revenue: p.amount_paid, cost: p.crew_cost + p.expense_total, profit: p.profit,
  }));

  return (
    <>
      <PageHeader title="Finance" subtitle="Budgets, expenses, and project profitability" />
      <div className="p-8 space-y-4 fade-up">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Invoiced" value={fmtINR(totalInvoiced)} />
          <Kpi label="Received" value={fmtINR(totalPaid)} tone="text-emerald-400" />
          <Kpi label="Total Costs" value={fmtINR(totalCrew + totalExp)} tone="text-white/70" sub={`Crew ${fmtINR(totalCrew)} · Exp ${fmtINR(totalExp)}`} />
          <Kpi label="Net Profit" value={fmtINR(netProfit)} tone={netProfit >= 0 ? "text-emerald-400" : "text-red-400"} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 hairline rounded-lg bg-[#0F0F0F] p-5">
            <div className="text-sm font-semibold mb-4">Revenue vs Cost per project</div>
            <div className="w-full h-[280px]">
              <ResponsiveContainer>
                <BarChart data={projectData}>
                  <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 11 }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                  <Bar dataKey="revenue" fill="#10B981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="cost" fill="#EF4444" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="hairline rounded-lg bg-[#0F0F0F] p-5">
            <div className="text-sm font-semibold mb-4">Expense Breakdown</div>
            <div className="w-full h-[220px]">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={catData} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={3}>
                    {catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#0F0F0F" strokeWidth={2} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5 mt-3">
              {catData.map((c, i) => (
                <div key={c.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[i] }} /><span className="text-white/70 capitalize">{c.name}</span></div>
                  <span className="mono">{fmtINR(c.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="hairline rounded-lg bg-[#0F0F0F] overflow-hidden">
          <div className="p-4 hairline-b text-sm font-semibold">Project P&L</div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-white/40">
              <tr className="hairline-b">
                <th className="text-left px-4 py-3">Project</th>
                <th className="text-right px-4 py-3">Invoice</th>
                <th className="text-right px-4 py-3">Received</th>
                <th className="text-right px-4 py-3">Crew</th>
                <th className="text-right px-4 py-3">Expenses</th>
                <th className="text-right px-4 py-3">Profit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {projects.map((p, i) => (
                <tr key={p.id} className={i % 2 === 0 ? "" : "bg-white/[0.01]"}>
                  <td className="px-4 py-3 font-medium">{p.name}<div className="text-[11px] text-white/50">{p.client_company}</div></td>
                  <td className="px-4 py-3 text-right mono">{fmtINR(p.invoice_amount)}</td>
                  <td className="px-4 py-3 text-right mono text-emerald-400">{fmtINR(p.amount_paid)}</td>
                  <td className="px-4 py-3 text-right mono">{fmtINR(p.crew_cost)}</td>
                  <td className="px-4 py-3 text-right mono">{fmtINR(p.expense_total)}</td>
                  <td className={`px-4 py-3 text-right mono font-bold ${p.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtINR(p.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Kpi({ label, value, sub, tone = "text-white" }) {
  return (
    <div className="hairline rounded-lg p-4 bg-[#0F0F0F]">
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`mono text-2xl font-bold mt-1 ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] text-white/40 mt-1 mono">{sub}</div>}
    </div>
  );
}
